/**
 * RegisterForm コンポーネント用テキストデータ
 * 日本語・英語対応のテキスト管理
 */

export const registerFormTexts = {
  ja: {
    // フォーム基本情報
    form: {
      requiredIndicator: '* は必須項目です。'
    },

    // フィールドラベル
    labels: {
      storeSelection: '店舗選択',
      name: 'お名前',
      nameKana: 'お名前（ひらがな）',
      email: 'メールアドレス',
      phoneNumber: '電話番号',
      postalCode: '郵便番号',
      prefecture: '都道府県',
      city: '市区町村',
      town: '町域・番地',
      building: '建物・部屋番号',
      birthDate: '生年月日',
      gender: '性別',
      desiredDate: '入会希望日',
      emergencyContactName: '緊急連絡先氏名',
      emergencyContactPhone: '緊急連絡先電話番号',
      paymentMethod: 'お支払方法',
      additionalStore: '利用追加希望店舗',
      shoeLocker: 'シューズロッカー申込（有料）',
      password: 'パスワード',
      passwordConfirm: 'パスワード（確認）'
    },

    // プレースホルダー
    placeholders: {
      storeSelection: '店舗を選択してください',
      name: '山田太郎',
      nameKana: 'やまだたろう',
      phoneNumber: '090-1234-5678',
      postalCode: '123-4567',
      emergencyContactName: '山田花子',
      emergencyContactPhone: '090-1234-5678',
      password: '8文字以上、英大文字、英小文字、数字含む',
      passwordConfirm: 'パスワードを再入力',
      additionalStore: '選択しない'
    },

    // 選択肢
    options: {
      gender: {
        male: '男性',
        female: '女性',
        other: 'その他'
      },
      paymentMethod: {
        creditCard: 'クレジットカード決済',
        bankTransfer: '口座振替'
      },
      shoeLocker: {
        apply: '申込する',
        decline: '申込しない',
        undecided: '未定'
      }
    },

    // ボタンテキスト
    buttons: {
      searchAddress: '住所検索',
      register: '会員登録',
      registering: '登録中...'
    },

    // ヘルプテキスト・説明文
    helpTexts: {
      storeNote: '※御徒町店は新規受付を一時停止しており再開時期は未定です。',
      nameKanaNote: 'ひらがなで入力してください。漢字、カタカナ、英数字は使用できません。',
      emailNote: '※このメールアドレスは認証済みのため変更できません',
      buildingNote: '集合住宅の場合、建物名、部屋番号のご記入を忘れずに',
      emergencyContactNote: `緊急連絡先は万が一事故等があった場合、運営者からご連絡させていただく先をご登録頂きたく存じます。
緊急時以外にご連絡することはございません。
未成年会員の方は保護者の方がご記入ください。同時に入会についての同意と保証に同意いただいたこととなります。`,
      paymentNote: '※口座振替の場合お手続きに時間がかかります。お急ぎの場合はクレジットカード決済をお勧めします。',
      additionalStoreNote: '※追加１店舗につき1000円（税別）の事務手数料が発生します。（カードキー発行店舗の場合別途カードキー発行費用が発生する場合がありますが、詳細は個別にご連絡いたします。）',
      shoeLockerNote: '※月額ロッカー費用は店舗により異なります。詳細はお問合せください。（330円～）'
    },

    // バリデーションメッセージ
    validation: {
      nameKanaError: 'ひらがなで入力してください（漢字、カタカナ、英数字は使用できません）',
      phoneNumberError: '正しい電話番号を入力してください（例：090-1234-5678）', 
      passwordMismatch: 'パスワードが一致しません',
      passwordFinalMismatch: 'パスワードが一致しません',
      postalCodeRequired: '郵便番号を入力してください',
      postalCodeFormat: '郵便番号は7桁の数字で入力してください'
    },

    // 郵便番号検索メッセージ
    postalSearch: {
      searching: '検索中...',
      success: '住所を取得しました',
      notFound: '住所が見つかりませんでした',
      failed: '住所の取得に失敗しました'
    },

    // 動的メッセージ（関数で生成）
    dynamic: {
      noShoeLockerAvailable: (storeName) => 
        `※選択された店舗（${storeName}）ではシューズロッカーはご利用いただけません。`,
      noShoeLockerStores: (storeNames) => 
        `※${storeNames.join('、')}にはシューズロッカーがございません。`
    }
  },

  en: {
    // 英語版のテキスト（将来の国際化対応用）
    form: {
      requiredIndicator: '* indicates required fields.'
    },

    labels: {
      storeSelection: 'Store Selection',
      name: 'Full Name',
      nameKana: 'Full Name (Hiragana)',
      email: 'Email Address',
      phoneNumber: 'Phone Number',
      postalCode: 'Postal Code',
      prefecture: 'Prefecture',
      city: 'City',
      town: 'Town/Address',
      building: 'Building/Room',
      birthDate: 'Date of Birth',
      gender: 'Gender',
      desiredDate: 'Desired Start Date',
      emergencyContactName: 'Emergency Contact Name',
      emergencyContactPhone: 'Emergency Contact Phone',
      paymentMethod: 'Payment Method',
      additionalStore: 'Additional Store',
      shoeLocker: 'Shoe Locker (Paid)',
      password: 'Password',
      passwordConfirm: 'Confirm Password'
    },

    placeholders: {
      storeSelection: 'Please select a store',
      name: 'Taro Yamada',
      nameKana: 'やまだたろう',
      phoneNumber: '090-1234-5678',
      postalCode: '123-4567',
      emergencyContactName: 'Hanako Yamada',
      emergencyContactPhone: '090-1234-5678',
      password: 'Enter 8+ characters',
      passwordConfirm: 'Re-enter password',
      additionalStore: 'None'
    },

    options: {
      gender: {
        male: 'Male',
        female: 'Female',
        other: 'Other'
      },
      paymentMethod: {
        creditCard: 'Credit Card',
        bankTransfer: 'Bank Transfer'
      },
      shoeLocker: {
        apply: 'Apply',
        decline: 'Decline',
        undecided: 'Undecided'
      }
    },

    buttons: {
      searchAddress: 'Search Address',
      register: 'Register',
      registering: 'Registering...'
    },

    validation: {
      nameKanaError: 'Please enter in Hiragana only (no Kanji, Katakana, or alphanumeric characters)',
      phoneNumberError: 'Please enter a valid phone number (e.g., 090-1234-5678)', 
      passwordMismatch: 'Passwords do not match',
      passwordFinalMismatch: 'Passwords do not match',
      postalCodeRequired: 'Please enter postal code',
      postalCodeFormat: 'Postal code must be 7 digits'
    }
  }
};

/**
 * 指定言語のテキストデータを取得
 * @param {string} language - 言語コード ('ja' | 'en')
 * @returns {Object} テキストデータ
 */
export const getRegisterFormTexts = (language = 'ja') => {
  return registerFormTexts[language] || registerFormTexts.ja;
};

/**
 * ネストされたパスからテキストを取得するヘルパー関数
 * @param {string} path - ドット記法のパス（例: 'labels.name'）
 * @param {string} language - 言語コード
 * @param {string} fallback - フォールバック文字列
 * @returns {string} 取得されたテキスト
 */
export const getRegisterFormText = (path, language = 'ja', fallback = '') => {
  const texts = getRegisterFormTexts(language);
  const keys = path.split('.');
  
  let result = texts;
  for (const key of keys) {
    if (result && result[key] !== undefined) {
      result = result[key];
    } else {
      return fallback;
    }
  }
  
  return typeof result === 'function' ? result : (result || fallback);
};

/**
 * 動的テキスト生成ヘルパー関数群
 */
export const registerFormTextHelpers = {
  /**
   * 必須フィールドマーク付きラベルを生成
   * @param {string} labelKey - ラベルキー
   * @param {boolean} required - 必須かどうか
   * @param {string} language - 言語
   * @returns {string} ラベル文字列
   */
  createFieldLabel: (labelKey, required = false, language = 'ja') => {
    const label = getRegisterFormText(`labels.${labelKey}`, language);
    return required ? `${label} *` : label;
  },

  /**
   * シューズロッカー利用不可メッセージ生成
   * @param {string} storeName - 店舗名
   * @param {string} language - 言語
   * @returns {string} メッセージ
   */
  createNoShoeLockerMessage: (storeName, language = 'ja') => {
    const texts = getRegisterFormTexts(language);
    return texts.dynamic?.noShoeLockerAvailable ? 
      texts.dynamic.noShoeLockerAvailable(storeName) : 
      `※選択された店舗（${storeName}）ではシューズロッカーはご利用いただけません。`;
  },

};

// デフォルトエクスポート
export default registerFormTexts;